import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
} from "@mui/material";
import {
  LocationOn,
  CalendarToday,
  LocalShipping,
  AttachMoney,
} from "@mui/icons-material";
// Items are rendered as text-only list

/**
 * QuoteDetails - Displays detailed quote information including service details and pricing
 * Mobile-first responsive design with clear information hierarchy
 */
export const QuoteDetails = ({ quote }) => {
  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    const formatted = new Intl.NumberFormat("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `ZAR ${formatted}`;
  };

  const safeFormatDate = (value) => {
    if (value === null || value === undefined) return "Not specified";
    const trimmed = String(value).trim();
    if (trimmed.length === 0) return "Not specified";
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) {
      // Not a concrete date (e.g., "Next week", "Urgently", "Not sure") → show as-is
      return trimmed;
    }
    return d.toLocaleDateString("en-ZA", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getServiceTypeColor = (type) => {
    const colors = {
      standard: "primary",
      premium: "secondary",
      express: "warning",
      custom: "info",
    };
    return colors[type] || "default";
  };

  if (!quote) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            Loading quote details...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          Service Details
        </Typography>

        {/* Route Information */}
        <Box mb={2}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LocationOn color="primary" />
            <Typography variant="subtitle2" color="primary">
              Route
            </Typography>
          </Stack>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body1" fontWeight="medium">
              From:
            </Typography>
            <Typography variant="body1">
              {quote.from || "Not specified"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1" fontWeight="medium">
              To:
            </Typography>
            <Typography variant="body1">
              {quote.to || "Not specified"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Service Date */}
        <Box mb={2}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <CalendarToday color="primary" />
            <Typography variant="subtitle2" color="primary">
              Service Date
            </Typography>
          </Stack>
          <Typography variant="body1">
            {safeFormatDate(
              quote.dates && quote.dates.length > 0
                ? quote.dates[0]
                : quote.date
            )}
          </Typography>
        </Box>

        {/* Items/Services - text only list */}
        <Divider sx={{ my: 2 }} />
        <Box mb={2}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LocalShipping color="primary" />
            <Typography variant="subtitle2" color="primary">
              Services Included
            </Typography>
          </Stack>
          {Array.isArray(quote.items) && quote.items.length > 0 ? (
            <List dense>
              {quote.items
                .filter(
                  (it) => it && (typeof it === "string" || it.type === "text")
                )
                .map((it, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <Typography variant="body2">
                      {typeof it === "string" ? it : it.content}
                    </Typography>
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No items specified
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Pricing Information */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <AttachMoney color="primary" />
            <Typography variant="subtitle2" color="primary">
              Pricing
            </Typography>
          </Stack>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            sx={(theme) => ({
              bgcolor: theme.palette.primary[50] || "primary.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: theme.palette.primary[200] || "primary.200",
            })}
          >
            <Typography variant="h6" color="primary">
              Total Amount:
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {formatCurrency(
                quote.totalCost !== undefined
                  ? quote.totalCost
                  : (quote.estimatedCost || 0) + (quote.addOnsCost || 0)
              )}
            </Typography>
          </Box>

          {quote.serviceType && (
            <Box mt={1} display="flex" justifyContent="center">
              <Chip
                label={`${quote.serviceType.toUpperCase()} Service`}
                color={getServiceTypeColor(quote.serviceType)}
                variant="outlined"
              />
            </Box>
          )}
        </Box>

        {/* Additional Information */}
        {quote.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Additional Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {quote.notes}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
